// src/contexts/ThemeContext.jsx
// ─────────────────────────────────────────────────────────────
//  App-wide theme, font-size, and display preferences.
//
//  Exports:
//    ThemeProvider       — wrap your app in this
//    useTheme()          — returns:
//      themeMode         {'light' | 'forest'}
//      changeThemeMode   {(mode: string) => void}
//      isForestTheme     {boolean}
//      fontSize          {'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge'}
//      changeFontSize    {(size: string) => void}
//      platformMode      {'auto' | 'ios' | 'android'}
//      changePlatformMode{(mode: string) => void}
//      uiScale           {'compact' | 'default' | 'comfortable'}
//      changeUiScale     {(scale: string) => void}
//      lineSpacing       {'tight' | 'default' | 'relaxed'}
//      changeLineSpacing {(spacing: string) => void}
//      buttonScale       {'small' | 'default' | 'large'}
//      changeButtonScale {(scale: string) => void}
//
//  Theme is applied by:
//    • Adding/removing the 'dark' class on <html>
//    • Setting data-* attributes on <html> for CSS overrides
//  All persisted via useSyncStore (IDB + Firestore sync).
// ─────────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import {
  STORE_THEME_MODE,
  STORE_FONT_SIZE,
  STORE_PLATFORM_MODE,
  STORE_UI_SCALE,
  STORE_LINE_SPACING,
  STORE_BUTTON_SCALE,
} from '@/lib/storeKeys';

const DEFAULT_THEME         = 'light';
const DEFAULT_FONT_SIZE     = 'small';
const DEFAULT_PLATFORM      = 'auto';
const DEFAULT_UI_SCALE      = 'default';
const DEFAULT_LINE_SPACING  = 'default';
const DEFAULT_BUTTON_SCALE  = 'default';

const ThemeContext = createContext(null);

// ── Detect native platform ────────────────────────────────────
function detectNativePlatform() {
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Mac/.test(navigator.platform) && 'ontouchend' in document) return 'ios';
  return 'android';
}

// ── Apply theme to <html> ─────────────────────────────────────
// Monotonic token: lets a later applyTheme() call invalidate any
// still-pending setTimeout retries queued by an earlier call (see
// below). Without this, a stale-mode timer firing after a newer
// correct-mode timer can silently flip the status bar back to the
// wrong color on cold start.
let themeApplyToken = 0;
// FIX (Flag 86): guards window.addEventListener('load', ...) below so it is
// only ever attached once per page lifetime, not on every applyTheme() call
// (i.e. every theme-mode change). 'load' fires exactly once, so re-adding it
// after the initial page load registered a listener that would never fire.
let loadListenerAttached = false;

function applyTheme(mode) {
  const token = ++themeApplyToken;
  const html = document.documentElement;
  if (mode === 'forest') html.classList.add('dark');
  else html.classList.remove('dark');

  // Match native status bar icon color to the active theme.
  // 'forest' = dark background → light (white) icons.
  // 'light'  = light background → dark icons.
  // No-op on web/PWA builds (isNativePlatform() is false there).
  import('@capacitor/core').then(({ Capacitor, SystemBars, SystemBarsStyle, SystemBarType }) => {
    if (!Capacitor.isNativePlatform()) return;
    const apply = () => {
      // A newer applyTheme() call has since started — this retry is
      // stale (queued by a superseded render) and must not overwrite
      // the status bar with an outdated mode. Bail silently.
      if (token !== themeApplyToken) return;
      SystemBars.setStyle({
        bar: SystemBarType.StatusBar,
        style: mode === 'forest' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
      }).catch(() => {});
    };
    // Cold start: the window/decor view isn't attached yet when this
    // effect first fires, so calling SystemBars.setStyle() immediately
    // silently no-ops (no error — it just doesn't visually apply).
    // A single double-rAF defer isn't reliably enough time on a cold
    // launch (slower devices, bridge still attaching), so retry at
    // increasing delays too. Calls are cheap/idempotent.
    requestAnimationFrame(() => requestAnimationFrame(apply));
    setTimeout(apply, 300);
    setTimeout(apply, 800);
    setTimeout(apply, 1500);
    // FIX: the Android 12+ system splash screen dismisses on first-frame
    // draw, not a fixed timer — timing varies by device/bundle load speed.
    // Any setStyle() call that lands while the splash is still up gets
    // clobbered the instant it hides and hands off to postSplashScreenTheme.
    // None of the fixed delays above are guaranteed to land after that
    // handoff on a slow cold start. 'load' fires once the page has fully
    // loaded, which reliably happens at/after the splash dismissal.
    if (!loadListenerAttached) {
      loadListenerAttached = true;
      window.addEventListener('load', apply, { once: true });
    }
    setTimeout(apply, 3000);
  }).catch(() => {});
}

// ── Apply font size to <html> ─────────────────────────────────
function applyFontSize(size) {
  document.documentElement.setAttribute('data-font-size', size);
}

// ── Apply platform mode ───────────────────────────────────────
function applyPlatformMode(mode) {
  const effective = mode === 'auto' ? detectNativePlatform() : mode;
  document.documentElement.setAttribute('data-platform', effective);
}

// ── Apply UI scale ────────────────────────────────────────────
function applyUiScale(scale) {
  document.documentElement.setAttribute('data-ui-scale', scale);
}

// ── Apply line spacing ────────────────────────────────────────
function applyLineSpacing(spacing) {
  document.documentElement.setAttribute('data-line-spacing', spacing);
}

// ── Apply button scale ────────────────────────────────────────
function applyButtonScale(scale) {
  document.documentElement.setAttribute('data-btn-scale', scale);
}

export function ThemeProvider({ children }) {
  const [themeMode, setThemeMode] = useSyncStore(STORE_THEME_MODE, DEFAULT_THEME);
  const [fontSize, setFontSize] = useSyncStore(STORE_FONT_SIZE, DEFAULT_FONT_SIZE);
  const [platformMode, setPlatformMode] = useSyncStore(STORE_PLATFORM_MODE, DEFAULT_PLATFORM);
  const [uiScale, setUiScale] = useSyncStore(STORE_UI_SCALE, DEFAULT_UI_SCALE);
  const [lineSpacing, setLineSpacing] = useSyncStore(STORE_LINE_SPACING, DEFAULT_LINE_SPACING);
  const [buttonScale, setButtonScale] = useSyncStore(STORE_BUTTON_SCALE, DEFAULT_BUTTON_SCALE);

  // ── Apply on first render and whenever values change ──────
  useEffect(() => { applyTheme(themeMode); }, [themeMode]);
  useEffect(() => { applyFontSize(fontSize); }, [fontSize]);
  useEffect(() => { applyPlatformMode(platformMode); }, [platformMode]);
  useEffect(() => { applyUiScale(uiScale); }, [uiScale]);
  useEffect(() => { applyLineSpacing(lineSpacing); }, [lineSpacing]);
  useEffect(() => { applyButtonScale(buttonScale); }, [buttonScale]);

  const changeThemeMode    = useCallback((v) => { setThemeMode(v); }, []);
  const changeFontSize     = useCallback((v) => { setFontSize(v); }, []);
  const changePlatformMode = useCallback((v) => { setPlatformMode(v); }, []);
  const changeUiScale      = useCallback((v) => { setUiScale(v); }, []);
  const changeLineSpacing  = useCallback((v) => { setLineSpacing(v); }, []);
  const changeButtonScale  = useCallback((v) => { setButtonScale(v); }, []);

  // ── Migrate old localStorage values to IDB on first load ──
  useEffect(() => {
    const oldTheme = localStorage.getItem('ds_theme_mode');
    if (oldTheme && oldTheme !== themeMode) setThemeMode(oldTheme);

    const oldFont = localStorage.getItem('ds_font_size');
    if (oldFont && oldFont !== fontSize) setFontSize(oldFont);

    const oldPlatform = localStorage.getItem('ds_platform_mode');
    if (oldPlatform && oldPlatform !== platformMode) setPlatformMode(oldPlatform);

    const oldUiScale = localStorage.getItem('ds_ui_scale');
    if (oldUiScale && oldUiScale !== uiScale) setUiScale(oldUiScale);

    const oldLineSpacing = localStorage.getItem('ds_line_spacing');
    if (oldLineSpacing && oldLineSpacing !== lineSpacing) setLineSpacing(oldLineSpacing);

    const oldButtonScale = localStorage.getItem('ds_button_scale');
    if (oldButtonScale && oldButtonScale !== buttonScale) setButtonScale(oldButtonScale);

    // Clean up old keys after migration
    localStorage.removeItem('ds_theme_mode');
    localStorage.removeItem('ds_font_size');
    localStorage.removeItem('ds_platform_mode');
    localStorage.removeItem('ds_ui_scale');
    localStorage.removeItem('ds_line_spacing');
    localStorage.removeItem('ds_button_scale');
  }, []); // Run once on mount

  const value = {
    themeMode, changeThemeMode, isForestTheme: themeMode === 'forest',
    fontSize, changeFontSize,
    platformMode, changePlatformMode,
    uiScale, changeUiScale,
    lineSpacing, changeLineSpacing,
    buttonScale, changeButtonScale,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}